import {
    type ArgumentsSchema,
    type FieldInputValue,
    type FilterFormType,
    type OpenPageActionConfig,
    type PageStateWithValue,
    type RecordLikeProtocol,
    type SelectedMode,
    type Sorter
} from '@lighthouse/core'
import type { ExtraParams } from '@lighthouse/shared'
import { asyncGenerateVariableValue, getMainTableRecordId, useAtomAction, useAtomData } from '@lighthouse/shared'
import { safeToString } from '@lighthouse/tools'
import { useAtomCallback } from 'jotai/utils'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import type { ExecuteActionParams } from '@/actionsEngine/type'
import { setLanguageAtom } from '@/atoms/application/action'
import { appAtom, AppSystemPageAtom } from '@/atoms/application/state'
import { fetchUserAtom } from '@/atoms/auth/action'
import { addRecordAtom, deleteRecordAtom, getRecordByFilterAtom } from '@/atoms/dataSource/action'
import { recordPoolAtom } from '@/atoms/dataSource/state'
import {
    fetchPageListAtom,
    openPageStackAtom,
    rollbackPageStackHistoryAtom,
    updatePageStateValueAtom,
    updateQueryParameterValueAtom
} from '@/atoms/page/action'
import { lastPageOfStackAtom, pageAtomFamily } from '@/atoms/page/state'
import { stackFactory } from '@/atoms/page/utils'
import { useCurrentPageContext, useCurrentStackIdContext, useRootPageContext } from '@/contexts/PageContext'
import * as srv from '@/services'
import type { GetRecordByFilterPayload } from '@/services/types'

import { useCurrentEnvId } from './useApplication'
import { useFloat } from './useFloat'
import { useParseVariable2Field4SelectDataSource } from './useParseVariable'

export interface ActionRuntimeParamsValue {
    actionEvents: {
        openPage: (
            payload: OpenPageActionConfig & {
                executeParams: ExecuteActionParams
            }
        ) => Promise<void>
        openSystemPage: (val: string) => void
        updatePageState: (payload: PageStateWithValue) => void
        updateQueryParameter: (payload: { queryParamId: string; value: string }) => void
        findSingleRecord: (payload: GetRecordByFilterPayload) => Promise<RecordLikeProtocol | undefined>
        getPageUrl: (
            payload: Omit<OpenPageActionConfig, 'pageOpenType'> & {
                executeParams: ExecuteActionParams
            }
        ) => Promise<string | undefined>
        closePage: () => void
        closeFloat: () => void
        refreshRootPage?: () => void
        refreshPage?: () => void
        refreshUserPermissions?: () => Promise<void>
        createRecord: (payload: RecordLikeProtocol) => void
        deleteRecord: (payload: {
            mode: SelectedMode
            dsId: string
            recordIds?: string[]
            filter?: FilterFormType
            sorts?: Sorter[]
        }) => Promise<boolean>
        updateRecord: (payload: {
            appId: string
            envId: string
            dsId: string
            recordId: string
            fields: {
                fieldId: string
                value: FieldInputValue | undefined
            }[]
        }) => Promise<RecordLikeProtocol>
    }
}

export const useActionRuntimeParams = () => {
    const { refreshPage } = useCurrentPageContext()
    const envId = useCurrentEnvId()
    const stackId = useCurrentStackIdContext()
    const parseSelectDataSourceVariable2Field = useParseVariable2Field4SelectDataSource()
    const { run: setLanguage } = useAtomAction(setLanguageAtom)
    const { run: popStack } = useAtomAction(rollbackPageStackHistoryAtom)
    const { run: fetchPageList } = useAtomAction(fetchPageListAtom)
    const { run: fetchUser } = useAtomAction(fetchUserAtom)
    const { run: setAppSystemPage } = useAtomAction(AppSystemPageAtom)
    const { refreshRootPage } = useRootPageContext()
    const { closeFloat } = useFloat()

    const host = useAtomData(
        appAtom,
        useCallback(s => {
            return s.config.domain.isUsedCustomDomain && s.config.domain.customDomain
                ? s.config.domain.customDomain
                : `${s.config.domain.domainPrefix}.${s.mainDomain}`
        }, [])
    )

    const updateRecord = useAtomCallback(
        useCallback(
            async (
                get,
                set,
                manualDeps: {
                    appId: string
                    envId: string
                    dsId: string
                    recordId: string
                    fields: {
                        fieldId: string
                        value: FieldInputValue | undefined
                    }[]
                }
            ) => {
                const { appId, envId, dsId, recordId, fields } = manualDeps
                const MainTableRecordId = getMainTableRecordId(recordId)
                const record = await srv.updateRecordWithFields({
                    appId,
                    envId,
                    dsId,
                    recordId,
                    fields
                })

                if (record) {
                    set(recordPoolAtom, draft => {
                        // 更新所有 mainTableRecordId 相同的记录（处理连接表）
                        for (const rec of draft.values()) {
                            if (
                                rec.appId === appId &&
                                rec.envId === envId &&
                                rec.dsId === dsId &&
                                getMainTableRecordId(rec.id) === MainTableRecordId
                            ) {
                                fields.forEach(f => {
                                    rec.content[f.fieldId] = { value: f.value }
                                })
                            }
                        }
                    })
                }
                return record
            },
            []
        )
    )

    const createRecord = useAtomCallback(
        useCallback((get, set, payload: RecordLikeProtocol) => {
            return set(addRecordAtom, payload)
        }, [])
    )

    const deleteRecord = useAtomCallback(
        useCallback(
            (
                get,
                set,
                {
                    mode,
                    dsId,
                    recordIds,
                    filter,
                    sorts
                }: { mode: SelectedMode; dsId: string; recordIds?: string[]; filter?: FilterFormType; sorts?: Sorter[] }
            ) => {
                return set(deleteRecordAtom, {
                    envId,
                    dsId,
                    recordIds,
                    filter,
                    sorts,
                    mode
                })
            },
            [envId]
        )
    )

    const openSystemPageCallBack = useCallback(
        (val: string) => {
            setAppSystemPage(val)
        },
        [setAppSystemPage]
    )

    // ✅ 优化：使用独立的 pageStateAtomFamily 更新页面状态
    const { run: updatePageStateValue } = useAtomAction(updatePageStateValueAtom)
    const updatePageStateCallback = useCallback(
        (payload: PageStateWithValue) => {
            const { id, value } = payload
            updatePageStateValue({ stackId, stateId: id, value })
        },
        [stackId, updatePageStateValue]
    )

    const { run: updateQueryParameterValue } = useAtomAction(updateQueryParameterValueAtom)
    const updateQueryParameterCallback = useCallback(
        (payload: { queryParamId: string; value: string }) => {
            const { queryParamId, value } = payload
            updateQueryParameterValue({ stackId, queryParamId, value })
        },
        [stackId, updateQueryParameterValue]
    )
    const getPageParams = useAtomCallback(
        useCallback(
            async (
                get,
                _,
                {
                    openPageId,
                    pathParameters,
                    queryParameters,
                    executeParams
                }: Omit<OpenPageActionConfig, 'pageOpenType'> & {
                    executeParams: ExecuteActionParams
                }
            ) => {
                const page = get(pageAtomFamily(openPageId))
                if (!page) {
                    return
                }
                setAppSystemPage(null)
                const extraParams = {
                    ...executeParams,
                    parseSelectDataSourceVariable2Field
                } as ExtraParams

                const pagePathSchemas = page.parameterAndVariable?.pathParameter
                const pagePathParameters =
                    pathParameters && pagePathSchemas
                        ? await Promise.all(
                              pagePathSchemas.map(async schema => {
                                  const parameter = pathParameters.find(item => item.id === schema.id)
                                  if (!parameter) {
                                      return ''
                                  }
                                  if (!parameter.value) {
                                      return ''
                                  }
                                  const result = await asyncGenerateVariableValue({
                                      innerType: schema.innerType,
                                      jsonContent: parameter.value,
                                      extraParams
                                  })
                                  return safeToString(result)
                              })
                          )
                        : undefined

                const pageQuerySchemas = page.parameterAndVariable?.queryParameter
                const queryParametersResult =
                    queryParameters && pageQuerySchemas
                        ? await Promise.all(
                              pageQuerySchemas.map(async schema => {
                                  const parameter = queryParameters.find(item => item.id === schema.id)

                                  if (!parameter) {
                                      return
                                  }

                                  if (!parameter.value) {
                                      // return [schema.id, '']
                                      return { ...schema, value: '' }
                                  }

                                  const data = await asyncGenerateVariableValue({
                                      innerType: schema.innerType,
                                      jsonContent: parameter.value,
                                      extraParams
                                  })

                                  // return [schema.id, String(data)]
                                  return { ...schema, value: safeToString(data) }
                              })
                          )
                        : []
                const queryResult = queryParametersResult.filter(Boolean) as (ArgumentsSchema & { value: string })[]
                return { page, pagePathParameters, queryResult }
            },
            [parseSelectDataSourceVariable2Field, setAppSystemPage]
        )
    )

    const getPageUrl = useCallback(
        async (
            payload: Omit<OpenPageActionConfig, 'pageOpenType'> & {
                executeParams: ExecuteActionParams
            }
        ) => {
            const pageParams = await getPageParams(payload)
            if (!pageParams) {
                return
            }
            const { page, pagePathParameters, queryResult } = pageParams
            const pathname = pagePathParameters?.join('/')
            const pageQueryParameters = new URLSearchParams(queryResult.map(item => [item.name, item.value]))
            const search = pageQueryParameters.toString()
            const pathSuffix = `${pathname ? `/${pathname}` : ''}${search ? `?${search}` : ''}`
            // const path = page.route ? `/${page.route}` : `/${page.id}`
            const path = page.route ? `/${page.route}` : ''
            return host + path + pathSuffix
        },
        [getPageParams, host]
    )

    const openPageAtomCallback = useAtomCallback(
        useCallback(
            async (
                get,
                set,
                payload: OpenPageActionConfig & {
                    executeParams: ExecuteActionParams
                }
            ) => {
                const { pageOpenType, hash } = payload
                const pageParams = await getPageParams(payload)
                if (!pageParams) {
                    return
                }
                const { page, queryResult, pagePathParameters } = pageParams
                setAppSystemPage(null)

                const pageQueryParameters = Object.fromEntries(queryResult.map(item => [item.id, item.value]))
                pageOpenType === 'page' && setLanguage(page.language)
                set(
                    openPageStackAtom,
                    stackFactory({
                        pageId: page.id,
                        pageStackDisplayType: pageOpenType,
                        pagePathParameters,
                        pageQueryParameters,
                        hash
                    })
                )
            },
            [getPageParams, setAppSystemPage, setLanguage]
        )
    )

    const findSingleRecord = useAtomCallback(
        useCallback((get, set, payload: GetRecordByFilterPayload) => {
            return set(getRecordByFilterAtom, payload)
        }, [])
    )

    const peekCurrentPageStack = useAtomCallback(useMemo(() => get => get(lastPageOfStackAtom), []))

    const refreshUserPermissions = useCallback(async () => {
        await Promise.all([fetchPageList(), fetchUser()])
    }, [fetchPageList, fetchUser])

    const actionEvents: ActionRuntimeParamsValue['actionEvents'] = useMemo(() => {
        return {
            openPage: openPageAtomCallback,
            openSystemPage: openSystemPageCallBack,
            updatePageState: updatePageStateCallback,
            updateQueryParameter: updateQueryParameterCallback,
            findSingleRecord,
            getPageUrl,
            peekCurrentPageStack,
            closePage: () => {
                popStack()
            },
            closeFloat,
            refreshPage,
            refreshRootPage,
            refreshUserPermissions,
            createRecord,
            deleteRecord,
            updateRecord
        }
    }, [
        openPageAtomCallback,
        openSystemPageCallBack,
        updatePageStateCallback,
        updateQueryParameterCallback,
        findSingleRecord,
        getPageUrl,
        peekCurrentPageStack,
        closeFloat,
        refreshPage,
        refreshRootPage,
        refreshUserPermissions,
        createRecord,
        deleteRecord,
        updateRecord,
        popStack
    ])

    const runtimeParamsRef = useRef<ActionRuntimeParamsValue | null>(null)

    useEffect(() => {
        runtimeParamsRef.current = null
    }, [actionEvents])

    const getActionRuntimeParams = useCallback((): ActionRuntimeParamsValue => {
        if (!runtimeParamsRef.current) {
            runtimeParamsRef.current = {
                actionEvents
            }
        }
        return runtimeParamsRef.current
    }, [actionEvents])

    return {
        getActionRuntimeParams
    }
}
